import React, { useEffect, useState } from 'react';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Activity, RefreshCw } from 'lucide-react';
import type { ScrapeRun } from '@/types';

// Scraper health at a glance. The Mac's launchd jobs write one scrapeRuns doc
// per run (Admin SDK); this reads the latest per adapter and flags anything
// stale, errored, warned, or stuck. Exists because ls-sync failed silently for
// a week in Sept 2026 while the team band kept a fresh timestamp.
const ADAPTERS: { key: ScrapeRun['adapter']; label: string; cadence: string; staleAfterHours: number }[] = [
  { key: 'leaguesecretary', label: 'LeagueSecretary scores', cadence: 'nightly · 6:15a + 10:15p', staleAfterHours: 26 },
  { key: 'stats-sync', label: 'Stats app snapshots + team band', cadence: 'nightly · 6:15a + 10:15p', staleAfterHours: 26 },
  { key: 'usbc-awards', label: 'USBC book history', cadence: 'weekly · Sunday 7:15a', staleAfterHours: 8 * 24 },
];

type Verdict = { tone: 'ok' | 'warn' | 'bad' | 'none'; headline: string; detail?: string };

const ago = (d?: Date | null) => {
  if (!d) return '';
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs} hr ago`;
  return `${Math.round(hrs / 24)} days ago`;
};
const when = (d?: Date | null) =>
  d ? d.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

const judge = (run: ScrapeRun | undefined, staleAfterHours: number): Verdict => {
  if (!run) return { tone: 'none', headline: 'No runs logged yet' };
  const end = run.finishedAt || run.startedAt;
  const ageHours = (Date.now() - end.getTime()) / 3600000;
  if (run.status === 'running') {
    return ageHours > 1
      ? { tone: 'bad', headline: `Started ${ago(run.startedAt)} and never finished`, detail: 'The script likely crashed mid-run. Check the Mac log.' }
      : { tone: 'ok', headline: `Running now (started ${ago(run.startedAt)})` };
  }
  if (run.status === 'error') return { tone: 'bad', headline: `Failed ${ago(end)}`, detail: run.error || 'No error text recorded.' };
  if (ageHours > staleAfterHours)
    return {
      tone: 'bad',
      headline: `No run since ${when(end)}`,
      detail: 'The scheduled job on the Mac is not running. On the Mac: launchctl list | grep com.dc.whs',
    };
  if (run.status === 'warn') return { tone: 'warn', headline: `Ran ${ago(end)} but found nothing to write`, detail: run.note || 'Check the Mac log for the raw LeagueSecretary rows.' };
  return { tone: 'ok', headline: `OK · ${ago(end)}` };
};

const DOT: Record<Verdict['tone'], string> = {
  ok: 'bg-green-500',
  warn: 'bg-amber-500',
  bad: 'bg-red-600',
  none: 'bg-tiger-neutral-300',
};

export const SyncHealthCard: React.FC = () => {
  const [runs, setRuns] = useState<ScrapeRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const snap = await getDocs(query(collection(db, 'scrapeRuns'), orderBy('startedAt', 'desc'), limit(60)));
      setRuns(
        snap.docs.map((d) => {
          const x = d.data();
          return {
            id: d.id,
            ...x,
            startedAt: x.startedAt?.toDate?.() || new Date(x.startedAt),
            finishedAt: x.finishedAt?.toDate?.() || (x.finishedAt ? new Date(x.finishedAt) : undefined),
          } as ScrapeRun;
        })
      );
    } catch (e) {
      console.error('scrapeRuns load failed', e);
      setError('Could not load the sync log.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const latest = (key: ScrapeRun['adapter']) => runs.find((r) => r.adapter === key);
  const worst = ADAPTERS.map((a) => judge(latest(a.key), a.staleAfterHours).tone);
  const overall: Verdict['tone'] = worst.includes('bad') ? 'bad' : worst.includes('warn') ? 'warn' : worst.every((t) => t === 'none') ? 'none' : 'ok';

  return (
    <div className="bg-white rounded-2xl shadow-tiger p-6">
      <div className="flex items-start justify-between gap-4 mb-1">
        <h3 className="text-lg font-bold text-tiger-primary-black flex items-center gap-2">
          <Activity className="w-5 h-5" /> Data sync
          <span className={`inline-block w-2.5 h-2.5 rounded-full ${DOT[overall]}`} aria-hidden />
        </h3>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1 text-xs font-semibold text-tiger-neutral-600 hover:text-tiger-primary-black disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>
      <p className="text-sm text-tiger-neutral-600 mb-4">
        Scores reach the site only when these jobs run on the Mac. Red means the site is going stale even if the numbers look fresh.
      </p>
      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
      <ul className="divide-y divide-tiger-neutral-100">
        {ADAPTERS.map((a) => {
          const run = latest(a.key);
          const v = judge(run, a.staleAfterHours);
          return (
            <li key={a.key} className="py-3 flex items-start gap-3">
              <span className={`mt-1.5 inline-block w-2.5 h-2.5 rounded-full shrink-0 ${DOT[v.tone]}`} aria-hidden />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                  <p className="font-semibold text-tiger-primary-black">{a.label}</p>
                  <p className="text-xs text-tiger-neutral-500">{a.cadence}</p>
                </div>
                <p className={`text-sm ${v.tone === 'bad' ? 'text-red-700 font-semibold' : v.tone === 'warn' ? 'text-amber-700' : 'text-tiger-neutral-700'}`}>
                  {v.headline}
                </p>
                {v.detail && <p className="text-xs text-tiger-neutral-600 mt-0.5 break-words">{v.detail}</p>}
                {run && v.tone !== 'bad' && (
                  <p className="text-xs text-tiger-neutral-500 mt-0.5">
                    {when(run.finishedAt || run.startedAt)}
                    {run.playersProcessed ? ` · ${run.playersProcessed} players` : ''}
                    {run.writes ? ` · ${run.writes} writes` : ''}
                    {run.adapter === 'leaguesecretary' && run.leaguesActive != null ? ` · ${run.leaguesActive} active leagues` : ''}
                    {run.fetchErrors ? ` · ${run.fetchErrors} fetch errors` : ''}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default SyncHealthCard;
